/**
 * Centralized route definitions for the survey application.
 * This module provides type-safe route generation and a single source of truth for all routes.
 * 
 * Usage:
 * ```typescript
 * import { routes } from '$lib/routes';
 * await goto(routes.survey.question(5));
 * ```
 */

/**
 * Survey-related routes
 */
const surveyRoutes = {
  /** Navigate to the survey start/root */
  start: () => '/',
  
  /** Navigate to a specific survey question */
  question: (id: number) => `/s/${id}`,
  
  /** Navigate to a survey result page */
  result: (id: number, status: string) => `/s/${id}/result?status=${status}`,
  
  /** Navigate to a survey result page (without status) */
  resultBase: (id: number) => `/s/${id}/result`,
} as const;

/**
 * Profile-related routes
 */
const profileRoutes = {
  /** Navigate to profile index */
  index: () => '/profile',
  
  /** Navigate to a specific profile with optional query parameters */
  withId: (id: string, queryParams?: Record<string, string>) => {
    const base = `/profile/${id}`;
    if (!queryParams || Object.keys(queryParams).length === 0) {
      return base;
    }
    const query = new URLSearchParams(queryParams).toString();
    return `${base}?${query}`;
  },
  
  /** Navigate to profile with encoded weights */
  withWeights: (id: string, weights: Record<string, number>) => {
    try {
      const json = JSON.stringify(weights);
      const b64 = btoa(unescape(encodeURIComponent(json)));
      return `/profile/${id}?w=${encodeURIComponent(b64)}`;
    } catch {
      return `/profile/${id}`;
    }
  },
} as const;

/**
 * Statistics/dashboard routes
 */
const statsRoutes = {
  /** Navigate to stats index */
  index: () => '/stats',

  /** Navigate to all stats */
  all: () => '/stats/all',

  /** Navigate to page analytics */
  analytics: () => '/stats/analytics',

  /** Navigate to a specific chart */
  chart: (id: number) => `/stats/charts/${id}`,
} as const;

/**
 * Authentication routes
 */
const authRoutes = {
  /** Navigate to auth page */
  index: () => '/auth',
} as const;

/**
 * Legacy survey routes (for external reference/documentation)
 * These are the old routes that were migrated to /s/
 */
const legacyRoutes = {
  /** Old survey question route */
  question: (id: number) => `/${id}`,
  
  /** Old survey result route */
  result: (id: number, status: string) => `/${id}/result?status=${status}`,
} as const;

/**
 * Main application routes
 */
const appRoutes = {
  /** Application root */
  home: () => '/',
  
  /** Dynamic survey loader route */
  surveyLoader: (surveyId: string, locale: string) => `/${surveyId}/${locale}`,
  
  /** Content API route for survey YAML */
  surveyContent: (surveyId: string, locale: string) => `/content/surveys/${surveyId}/${locale}.yaml`,
} as const;

/**
 * Centralized routes object - the main export
 */
export const routes = {
  /** Main application routes */
  app: appRoutes,
  
  /** Survey flow routes */
  survey: surveyRoutes,
  
  /** Profile routes */
  profile: profileRoutes,
  
  /** Statistics/dashboard routes */
  stats: statsRoutes,
  
  /** Authentication routes */
  auth: authRoutes,
  
  /** Legacy routes (for reference) */
  legacy: legacyRoutes,
} as const;

/**
 * Helper function to add query parameters to any route
 */
export function addQueryParams(path: string, params: Record<string, string | number | boolean>): string {
  const url = new URL(path, 'http://example.com'); // Base URL doesn't matter, we only use pathname and search
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      url.searchParams.set(key, String(value));
    }
  });
  return url.pathname + url.search;
}

/**
 * Helper function to preserve preview mode across navigation
 */
export function withPreviewMode(path: string, isPreviewMode: boolean): string {
  return isPreviewMode ? addQueryParams(path, { preview: true }) : path;
}

/**
 * Type definitions for route parameters
 */
export interface RouteParams {
  questionId?: number;
  surveyId?: string;
  locale?: string;
  profileId?: string;
  status?: string;
  preview?: boolean;
}

/**
 * Route matcher patterns for use in layout guards and middleware
 */
export const routePatterns = {
  /** Pattern to match main survey routes */
  mainSurvey: /^\/s\/\d+(?:\/)?.*$/,
  
  /** Pattern to match any survey route including root */
  anySurvey: /^\/$|^\/s\/\d+(?:\/)?.*$/,
  
  /** Pattern to match profile routes */
  profile: /^\/profile(?:\/.*)?$/,
  
  /** Pattern to match stats routes */
  stats: /^\/stats(?:\/.*)?$/,
  
  /** Check if a path is a main survey route */
  isMainSurveyRoute: (path: string): boolean => {
    return path === '/' || routePatterns.mainSurvey.test(path);
  },
} as const;

export default routes;